import { Injectable, Signal, computed, signal } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { ServerConnectionService } from '../server-connection.service';
import { UtilitiesService } from '../utilities.service';
import { CoreModule } from '../../core.module';
import { IPromocode } from '../../../types/datatypes';
import { ApplyPromoCodeResponse, PreviewPromoCodeResponse, PromocodeAPIResponse } from '../../../types/responses';

@Injectable({
	providedIn: CoreModule,
})
export class PromoCodeService {
	/**
	 * Signal-based store for the membership promo code, kept separate from
	 * the ReplaySubject stores below by design, we eventually want to migrate the
	 * whole thing to signals but for now we limit the blast radius.
	 */
	public readonly membershipCreditRemaining: Signal<number> = computed(() => {
		const promoCode = this._membershipPromoCode();
		if (!promoCode) return 0;
		return Math.max(promoCode.discountLimit - promoCode.currentLimit, 0);
	});

	public readonly hasMembershipCredit: Signal<boolean> = computed(() => this.membershipCreditRemaining() > 0);

	private _promoCodeById = new ReplaySubject(1);

	private _promoCodeByCode = new ReplaySubject(1);

	private _promoCodeStore = new ReplaySubject<Array<any>>(1);

	private _updatePromoCode = new Subject<boolean>();

	private readonly _membershipPromoCode = signal<IPromocode | null>(null);

	private dataStore = {
		promoCode: {
			byId: {},
			byCode: {},
			store: [],
		},
	};

	constructor(
		private scs: ServerConnectionService,
		private us: UtilitiesService,
	) {
		this._promoCodeById.next({});
	}

	get promoCodeById$() {
		return this._promoCodeById.asObservable();
	}

	get promoCodeByCode$() {
		return this._promoCodeByCode.asObservable();
	}

	get promoCodeStore() {
		return this._promoCodeStore.asObservable();
	}

	get updatePromoCode() {
		return this._updatePromoCode.asObservable();
	}

	triggerUpdatePromoCode() {
		this._updatePromoCode.next(true);
	}

	public checkCode(promoCode, siteAdvert, email, referrer, user) {
		let query;
		if (user) {
			query = `user=${user}`;
		}
		return this.scs.http$('GET', `/api/promo-codes/${promoCode}/${siteAdvert}/${email}/${referrer}`, query, null);
	}

	public getMembershipPromocode(username: string): Observable<PromocodeAPIResponse> {
		return this.scs.http$('GET', `/api/subscriptions/promocode/${username}`, null, null) as Observable<PromocodeAPIResponse>;
	}

	public loadMembershipPromoCode(username: string): void {
		this.getMembershipPromocode(username).subscribe((result) => {
			if (result?.success && result.promoCode) {
				this._membershipPromoCode.set(result.promoCode);
			}
		});
	}

	public previewPromostack(code: string, promoStackId: string) {
		return this.scs.post$<PreviewPromoCodeResponse>('/api/promo-codes/preview', {
			code,
			promoStackId,
		});
	}

	public apply(code: string, targetId: string, type: 'promoStack' | 'featuredBook') {
		return this.scs.post$<ApplyPromoCodeResponse>('/api/promo-codes/apply', {
			code,
			targetId,
			type,
		});
	}

	public fetchCodeByReferrer(referrer) {
		return this.scs.http$('GET', `/api/promo-codes/by-referrer/${referrer}`, null, null);
	}

	/**
	 * @deprecated
	 */
	public logUse(promoCodeId, promoCodeCurrentLimit) {
		return this.scs.http$('PUT', `/api/promo-codes/log-use/${promoCodeId}`, null, promoCodeCurrentLimit);
	}
}
